import { useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  LangMode,
  SLIDER_TICKS,
  PLAN_I18N,
  CardContent,
  FeatureSection,
  PartnerDiscountItem,
} from "./planSelectionData";
import PaymentModal, { type PackageSummary } from "./PaymentModal";

export type { LangMode };

export interface PlanSelectionStepProps {
  lang?: LangMode;
  onSelectPlan?: (planId: string) => void;
  onSkip?: () => void;
  loading?: boolean;
  hideHeaderTitle?: boolean;
  defaultPlanId?: string;
}

type CardId = "starter" | "growth" | "enterprise";

/** Small per-plan accent: icon + colour. Everything else stays neutral and clean. */
const PLAN_ICON: Record<CardId, { icon: string; color: string }> = {
  starter: { icon: "ri-seedling-fill", color: "text-[#14b8a6]" },
  growth: { icon: "ri-vip-diamond-fill", color: "text-[#2f51d6]" },
  enterprise: { icon: "ri-vip-crown-2-fill", color: "text-[#f59e0b]" },
};

const BRAND = "#2f51d6";

export default function PlanSelectionStep({
  lang = "en",
  onSelectPlan,
  onSkip,
  loading = false,
  hideHeaderTitle = false,
  defaultPlanId,
}: PlanSelectionStepProps) {
  const navigate = useNavigate();
  const t = PLAN_I18N[lang] || PLAN_I18N.en;
  const isKm = lang === "km";

  const [userType, setUserType] = useState<"single" | "chain">("single");
  const [duration, setDuration] = useState<"month" | "1yr" | "2yr">("2yr");
  const [activeCardId, setActiveCardId] = useState<CardId>(
    defaultPlanId?.includes("starter") ? "starter" : defaultPlanId?.includes("enterprise") ? "enterprise" : "growth"
  );

  // Card 1 option
  const [card1Tier, setCard1Tier] = useState<"std" | "adv">("std");
  // Sliders for Cards 2 and 3
  const [card2Tick, setCard2Tick] = useState<number>(2); // 1K default
  const [card3Tick, setCard3Tick] = useState<number>(0); // 0.3K default

  const card2Data = SLIDER_TICKS[card2Tick];
  const card3Data = SLIDER_TICKS[card3Tick];

  const card1Price = card1Tier === "std" ? 269 : 499;
  const card1Orig = card1Tier === "std" ? 899 : 1599;

  const durationLabel =
    duration === "2yr"
      ? "36 mos (2 Yrs + 1 Yr Free)"
      : duration === "1yr"
      ? "12 mos (1 Year)"
      : "Monthly";

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState<PackageSummary>({
    id: "growth_3000",
    name: "Growth Pro",
    subtitle: "36 mos (2 Yrs + 1 Yr Free) · 3,000 Active Members / mo",
    price: 6899,
  });

  const handleOpenPayment = (cardId: "starter" | "growth" | "enterprise") => {
    setActiveCardId(cardId);
    if (cardId === "starter") {
      setSelectedPkg({
        id: "starter",
        name: t.card1.title,
        subtitle: `${durationLabel} · ${card1Tier === "std" ? "1 Outlet · 100 tx/mo" : "3 Outlets · 300 tx/mo"}`,
        price: card1Price,
      });
    } else if (cardId === "growth") {
      setSelectedPkg({
        id: `growth_${card2Data.members}`,
        name: t.card2.title,
        subtitle: `${durationLabel} · ${card2Data.members} Active Members / mo`,
        price: card2Data.card2Price,
      });
    } else {
      setSelectedPkg({
        id: `enterprise_${card3Data.members}`,
        name: t.card3.title,
        subtitle: `${durationLabel} · ${t.card3Branches(card3Data.members)}`,
        price: card3Data.card3Price,
      });
    }
    setPaymentModalOpen(true);
  };

  const handleSelect = (planKey: string) => {
    if (onSelectPlan) onSelectPlan(planKey);
    else navigate(`/login?plan=${planKey}`);
  };

  // ---- pieces ----------------------------------------------------------

  const renderSlider = (id: CardId, tick: number, setTick: (n: number) => void, label: string, monthly: string) => {
    const pct = (tick / (SLIDER_TICKS.length - 1)) * 100;
    return (
      <div className="mt-4">
        <div className="relative h-5 flex items-center">
          <input
            type="range"
            min="0"
            max={SLIDER_TICKS.length - 1}
            step="1"
            value={tick}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              setActiveCardId(id);
              setTick(Number(e.target.value));
            }}
            className="absolute inset-0 w-full opacity-0 cursor-pointer z-10"
            aria-label={label}
          />
          <div className="relative h-1 w-full rounded-full bg-[#e5e6eb]">
            <div className="absolute inset-y-0 left-0 rounded-full bg-[#1f2329]" style={{ width: `${pct}%` }} />
            <span
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white shadow"
              style={{ left: `${pct}%`, border: `3px solid ${BRAND}` }}
            />
          </div>
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-[#a3a8b0]">
          {SLIDER_TICKS.map((st, i) => (
            <button
              key={st.label}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveCardId(id);
                setTick(i);
              }}
              className={`cursor-pointer ${i === tick ? "font-bold text-[#1f2329]" : "hover:text-[#1f2329]"}`}
            >
              {st.label}
            </button>
          ))}
        </div>
        <div className="mt-3 text-xl font-bold text-[#1f2329] leading-tight">{label}</div>
        <div className="text-[11px] text-[#8f959e]">
          {t.currency}
          {monthly}
          {t.perMonth}
        </div>
      </div>
    );
  };

  const renderSections = (sections: FeatureSection[]) => (
    <div className="mt-5 pt-5 border-t border-[#f0f1f3] space-y-5">
      {sections.map((section, sIdx) => (
        <div key={sIdx}>
          <div className="flex items-center gap-1.5 mb-2">
            <h4 className={`font-bold text-[#1f2329] text-[13px] ${isKm ? "leading-normal" : ""}`}>{section.title}</h4>
            {section.badge && (
              <span className="text-[10px] px-1.5 py-px rounded bg-[#f2f3f5] text-[#646a73]">{section.badge}</span>
            )}
          </div>
          <ul className="space-y-1.5">
            {section.items.map((item, iIdx) => (
              <li key={iIdx} className="flex items-start gap-2">
                <i
                  className={`${item.checked ? "ri-check-line text-[#1f2329]" : "ri-close-line text-[#c9cdd4]"} text-sm leading-[18px] shrink-0`}
                />
                <span
                  className={`text-xs leading-[18px] ${item.checked ? "text-[#41464f]" : "text-[#a3a8b0]"} ${isKm ? "leading-relaxed" : ""}`}
                >
                  {item.text}
                  {item.boldPart && <span className="ml-1 font-semibold" style={{ color: item.checked ? BRAND : undefined }}>{item.boldPart}</span>}
                </span>
              </li>
            ))}
          </ul>
          {section.footerNote && <p className="text-[10px] text-[#a3a8b0] mt-1.5">{section.footerNote}</p>}
        </div>
      ))}
    </div>
  );

  const renderPerks = (title: string, banner: string, items: PartnerDiscountItem[]) => (
    <div className="mt-5">
      <div className="text-[13px] font-bold text-[#1f2329] mb-2">{title}</div>
      <div className="rounded-xl border border-[#adc2ff] bg-[#f5f8ff] p-3">
        <div className="flex items-center gap-1.5 text-xs font-bold" style={{ color: BRAND }}>
          <i className="ri-gift-2-fill" /> {banner}
        </div>
        <div className="mt-2 space-y-1.5">
          {items.map((pd, pIdx) => (
            <div key={pIdx} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="text-[#41464f] truncate">{pd.name}</span>
              <span className="shrink-0 font-semibold" style={{ color: BRAND }}>
                {pd.discount}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCard = (opts: {
    id: CardId;
    copy: CardContent;
    price: number;
    original: number;
    control: ReactNode;
    planKey: string;
    popular?: boolean;
  }) => {
    const { id, copy, price, original, control, planKey, popular } = opts;
    const active = activeCardId === id;
    const off = Math.round((1 - price / original) * 100);
    const accent = PLAN_ICON[id];

    return (
      <div
        key={id}
        onClick={() => handleOpenPayment(id)}
        className={`relative rounded-2xl bg-white cursor-pointer transition-all flex flex-col ${
          active ? "ring-2 shadow-[0_12px_32px_-18px_rgba(47,81,214,0.55)]" : "ring-1 ring-[#eceef2] hover:ring-[#d5d9e0]"
        }`}
        style={active ? ({ "--tw-ring-color": BRAND } as CSSProperties) : undefined}
      >
        {popular && (
          <div className="rounded-t-2xl text-center text-[11px] font-semibold text-white py-1.5" style={{ background: BRAND }}>
            {copy.tierBadge} · {t.durationGiftBadge}
          </div>
        )}

        <div className="p-5 sm:p-6 flex flex-col flex-1">
          {/* Title row */}
          <div className="flex items-start justify-between gap-2">
            <h3 className={`flex items-center gap-1.5 text-lg font-bold text-[#1f2329] ${isKm ? "leading-snug" : "tracking-tight"}`}>
              <i className={`${accent.icon} ${accent.color} text-base`} />
              {copy.title}
            </h3>
            <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold text-white bg-[#f54a45]">-{off}%</span>
          </div>
          <p className="mt-1 text-[11px] text-[#8f959e] leading-relaxed min-h-[30px]">{copy.desc}</p>

          {/* Price */}
          <div className="mt-4 flex items-baseline gap-1">
            <span className="text-lg font-bold text-[#1f2329]">{t.currency}</span>
            <span className="text-[40px] leading-none font-extrabold text-[#1f2329] tracking-tight">{price.toLocaleString()}</span>
            <span className="text-xs text-[#646a73]">{copy.priceUnit}</span>
            <span className="text-xs text-[#c9cdd4] line-through ml-1">
              {t.currency}
              {original.toLocaleString()}
            </span>
          </div>
          <div className="text-[11px] text-[#a3a8b0] mt-1">{copy.termNote}</div>

          {control}

          <button
            type="button"
            disabled={loading}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenPayment(id);
            }}
            className="w-full mt-4 h-10 rounded-lg text-sm font-semibold text-white cursor-pointer transition-all hover:brightness-110 disabled:opacity-60"
            style={{ background: active ? BRAND : "#1f2329" }}
          >
            {loading ? t.processing : copy.ctaText}
          </button>
          <p className="text-[11px] text-[#8f959e] text-center mt-2">{copy.subNote}</p>

          {renderSections(copy.sections)}
          <div className="mt-auto">{renderPerks(copy.partnerDiscountsTitle, copy.partnerDiscountsBanner, copy.partnerDiscounts)}</div>
        </div>
      </div>
    );
  };

  const starterControl = (
    <div className="mt-4">
      <div className="text-xs font-semibold text-[#1f2329] mb-2">{t.card1.tierBadge}</div>
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-[#f2f3f5] p-1 text-xs">
        {(["std", "adv"] as const).map((tier) => (
          <button
            key={tier}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActiveCardId("starter");
              setCard1Tier(tier);
            }}
            className={`h-8 rounded-md cursor-pointer transition-all ${
              card1Tier === tier ? "bg-white text-[#1f2329] font-semibold shadow-sm" : "text-[#646a73]"
            }`}
          >
            {tier === "std" ? t.card1TierStd : t.card1TierAdv}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-[#8f959e] mt-2">{card1Tier === "std" ? t.card1Spec : t.card1SpecAdv}</p>
    </div>
  );

  return (
    <div lang={lang} className={`w-full max-w-6xl mx-auto py-2 sm:py-4 px-3 sm:px-6 space-y-6 ${isKm ? "font-khmer" : "font-sans"}`}>
      {/* Header + toggles */}
      <div className="text-center space-y-4">
        {!hideHeaderTitle && (
          <h1 className={`text-xl sm:text-2xl text-[#1f2329] flex items-center justify-center gap-2 ${isKm ? "font-bold" : "font-extrabold tracking-tight"}`}>
            <span>{t.headline}</span>
            <span className="text-[#f54a45] font-extrabold">{t.discountBadge}</span>
          </h1>
        )}

        {/* Segment: underline tabs */}
        <div className="flex justify-center gap-6 text-sm">
          {(["single", "chain"] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setUserType(type)}
              className={`pb-1.5 border-b-2 cursor-pointer transition-colors ${
                userType === type ? "border-[#1f2329] text-[#1f2329] font-semibold" : "border-transparent text-[#8f959e] hover:text-[#1f2329]"
              }`}
            >
              {type === "single" ? t.tabSingle : t.tabChain}
            </button>
          ))}
        </div>

        {/* Duration: pill group */}
        <div className="inline-flex p-1 rounded-full bg-[#eceef1] text-xs">
          {(
            [
              ["month", t.durationMonth],
              ["1yr", t.duration1Yr],
              ["2yr", t.duration2Yr],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setDuration(key)}
              className={`h-8 px-4 rounded-full cursor-pointer inline-flex items-center gap-1 transition-all ${
                duration === key ? "bg-white text-[#1f2329] font-semibold shadow-sm" : "text-[#646a73] hover:text-[#1f2329]"
              }`}
            >
              {label}
              {key === "2yr" && <span className="text-[#f54a45] font-semibold">{t.durationGiftBadge}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Three plan cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch relative z-10">
        {renderCard({
          id: "starter",
          copy: t.card1,
          price: card1Price,
          original: card1Orig,
          control: starterControl,
          planKey: "starter",
        })}
        {renderCard({
          id: "growth",
          copy: t.card2,
          price: card2Data.card2Price,
          original: card2Data.card2Original,
          control: renderSlider("growth", card2Tick, setCard2Tick, t.card2Members(card2Data.members), card2Data.card2Monthly),
          planKey: `growth_${card2Data.members}`,
          popular: true,
        })}
        {renderCard({
          id: "enterprise",
          copy: t.card3,
          price: card3Data.card3Price,
          original: card3Data.card3Original,
          control: renderSlider("enterprise", card3Tick, setCard3Tick, t.card3Branches(card3Data.members), card3Data.card3Monthly),
          planKey: `enterprise_${card3Data.members}`,
        })}
      </div>

      {onSkip && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={onSkip}
            className="text-[#8f959e] hover:text-[#1f2329] text-xs font-medium cursor-pointer py-1 px-3 rounded-full hover:bg-[#f2f3f5]"
          >
            {t.skipBtn}
          </button>
        </div>
      )}
      {/* ========================================================================= */}
      {/* 4. PAYMENT POPUP MODAL (Matches Image 1: ABA KHQR, Card, Alipay, WeChat, TG) */}
      {/* ========================================================================= */}
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        pkg={selectedPkg}
        onSuccess={(planId) => {
          setPaymentModalOpen(false);
          handleSelect(planId);
        }}
        lang={lang}
      />
    </div>
  );
}